/* eslint-disable max-lines-per-function */
import { ReactElement, useCallback } from 'react';

import { CompleteAddress } from '@dreryk/address-common-api';
import {
   ServiceProvidingLocation,
   ServiceProvidingLocationForCurrentPracticeQuery,
   ServiceProvidingLocationUpdateCommand,
   ServiceProvidingLocationUpdateUsingPracticeDataCommand,
} from '@dreryk/egabinet-healthcare-provider-frontend-api';

import { performCqrsQueryGet, useCqrsCommand } from '@dreryk/command-query-react-common';
import { dispatchSnackbarPositiveEvent } from '@dreryk/react-components';
import { Choice, Slot } from '@dreryk/watermelon';
import { PromiseVoidFunction } from '@dreryk/react-common';

import { serviceProvidingLocationTranslationKeys } from '../../../../../translations';
import { HealthcarePracticeFormBean } from '../../../common/healthcarePractice';
import { ServiceProvidingLocationFormBean } from '../../../common/serviceProvidingLocation';
import { ServiceProvidingLocationUpdateLink } from '../../../serviceProvidingLocationUpdate/ServiceProvidingLocationUpdateLink';

type ServiceProvidingLocationFromServerUpdateCellProps = Pick<ServiceProvidingLocation, 'id'> & {
   readonly healthcarePractice: HealthcarePracticeFormBean;
   readonly refetchServiceProvidingLocations: PromiseVoidFunction;
};

export const ServiceProvidingLocationFromServerUpdateCell = (
   props: ServiceProvidingLocationFromServerUpdateCellProps
): ReactElement => {
   const prepareInitialData = useCallback(async (): Promise<ServiceProvidingLocationFormBean> => {
      const serviceProvidingLocation = await performCqrsQueryGet<ServiceProvidingLocation>(() => {
         const query = new ServiceProvidingLocationForCurrentPracticeQuery();
         query.id = Slot.of(props.id);
         return query;
      });

      return {
         id: serviceProvidingLocation.id,
         locationCode: serviceProvidingLocation.locationCode ?? null,
         city: serviceProvidingLocation.address.findTown()?.getName() ?? null,
         emailAddress: serviceProvidingLocation.emailAddress ?? null,
         flat: serviceProvidingLocation.address.findFlat()?.asString() ?? null,
         house: serviceProvidingLocation.address.findBuilding()?.asString() ?? null,
         serviceProvidingLocationName: serviceProvidingLocation.name ?? null,
         phoneNumber: serviceProvidingLocation.phoneNumber ?? null,
         postcode: serviceProvidingLocation.address.findPostcode() ?? null,
         street: serviceProvidingLocation.address.findStreet()?.asString() ?? null,
         usingHealthcarePracticeDetails: serviceProvidingLocation.usingHealthcarePracticeDetails,
         medicalField: serviceProvidingLocation.medicalField ?? null,
      };
   }, [props.id]);

   const { executeAsync: updateServiceProvidingLocation } = useCqrsCommand<
      ServiceProvidingLocation,
      ServiceProvidingLocationFormBean
   >(
      (parameters) => {
         const command = new ServiceProvidingLocationUpdateCommand();
         command.id = parameters.id!;
         command.emailAddress = Choice.withUseDefaultIfNull(parameters.emailAddress);
         command.name = parameters.serviceProvidingLocationName!;
         command.medicalFieldId = Choice.withUseDefaultIfNull(parameters.medicalField?.id ?? null);
         command.phoneNumber = parameters.phoneNumber!;
         command.address = CompleteAddress.builder()
            .withTown(parameters.city!)
            .withPostcode(parameters.postcode!)
            .withStreet(parameters.street)
            .withBuilding(parameters.house!)
            .withFlat(parameters.flat)
            .build();

         return command;
      },
      {
         onSuccess: (): void => {
            props.refetchServiceProvidingLocations();
            dispatchSnackbarPositiveEvent(
               serviceProvidingLocationTranslationKeys.updateServiceProvidingLocationDataSuccess
            );
         },
      }
   );
   const { executeAsync: updateServiceProvidingLocationUpdateUsingPracticeData } = useCqrsCommand<
      ServiceProvidingLocation,
      ServiceProvidingLocationFormBean
   >(
      (parameters) => {
         const command = new ServiceProvidingLocationUpdateUsingPracticeDataCommand();
         command.id = parameters.id!;
         command.medicalFieldId = Choice.withUseDefaultIfNull(parameters.medicalField?.id ?? null);
         return command;
      },
      {
         onSuccess: (): void => {
            props.refetchServiceProvidingLocations();
            dispatchSnackbarPositiveEvent(
               serviceProvidingLocationTranslationKeys.updateServiceProvidingLocationDataSuccess
            );
         },
      }
   );
   const handleUpdate = useCallback(
      async (formData: ServiceProvidingLocationFormBean) => {
         if (formData.usingHealthcarePracticeDetails) {
            return updateServiceProvidingLocationUpdateUsingPracticeData(formData);
         }

         return updateServiceProvidingLocation(formData);
      },
      [updateServiceProvidingLocation, updateServiceProvidingLocationUpdateUsingPracticeData]
   );

   return (
      <ServiceProvidingLocationUpdateLink
         healthcarePractice={props.healthcarePractice}
         prepareInitialData={prepareInitialData}
         onUpdate={handleUpdate}
      />
   );
};
